import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StorageMap } from '@ngx-pwa/local-storage';
import { DashboardService } from '../../dashboard/dashboard.service';

@Component({
  selector: 'app-candidate-welcome',
  templateUrl: './candidate-welcome.component.html',
  styleUrls: ['./candidate-welcome.component.scss']
})
export class CandidateWelcomeComponent implements OnInit {

  assessmentInfo: any = {
    candidateName: '',
    email: '',
    name: '',
    duration: null,
    welcomeTemplate: ''
  };
  assessmentId: any;
  isEnterpriseUser: any = false;
  placeholderDummies: { [key: string]: any } = {};

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private storage: StorageMap,
    private dashboardService: DashboardService) {
    this.assessmentInfo = [];
    this.assessmentId = this.activatedRoute.snapshot.paramMap.get('id');
    this.isEnterpriseUser = this.activatedRoute.snapshot.queryParamMap.get('enterprise');
  }

  ngOnInit() {
    this.initializePlaceholders();

    /** TODO Need to take data from previous page */
    this.storage.get('welcomeViewData').subscribe((assessmentInfo: any) => {
      if (assessmentInfo) {
        this.assessmentInfo = {
          candidateName: assessmentInfo?.candidateName,
          email: assessmentInfo?.email,
          name: assessmentInfo?.assessments[0]?.name,
          duration: assessmentInfo?.assessments[0]?.duration,
          welcomeTemplate: assessmentInfo?.assessments[0]?.welcomeTemplate
        };
        this.assessmentId = assessmentInfo?.assessments[0]?.assessmentId;
        this.updatePlaceholders();
      }
    });

    this.storage.get('assessments').subscribe((assessments: any) => {
      if (assessments) {
        let currentAssessment = assessments?.assessments?.filter((item) => item.assessmentId === this.assessmentId);
        this.assessmentInfo = {
          candidateName: assessments?.candidateName,
          email: assessments?.email,
          name: currentAssessment[0]?.name,
          duration: currentAssessment[0]?.duration,
          welcomeTemplate: currentAssessment[0]?.welcomeTemplate
        };
        this.updatePlaceholders();
      }
    });
  }

  private initializePlaceholders(): void {
    // Initialize with default values
    this.placeholderDummies = {
      '{{candidate_name}}': 'Candidate',
      '{{CANDIDATE_NAME}}': 'Candidate',
      '{{company_name}}': 'Company',
      '{{COMPANY_NAME}}': 'Company',
      '{{enterprise_name}}': 'Company',
      '{{job_title}}': 'Position',
      '{{JOB_TITLE}}': 'Position',
      '{{company_email}}': 'support@company.com',
      '{{COMPANY_EMAIL}}': 'support@company.com',
      '{{company_phone}}': 'N/A',
      '{{COMPANY_PHONE}}': 'N/A',
      '{{duration}}': '60 mins',
      '{{DURATION}}': '60 mins'
    };
  }

  private updatePlaceholders(): void {
    const substitutions: { [key: string]: string } = {
      candidateName: this.assessmentInfo?.candidateName || 'Candidate',
      assessmentName: this.assessmentInfo?.name || 'Assessment',
      duration: this.formatMins(this.assessmentInfo?.duration ?? 60),
      jobTitle: 'Position', // Default
    };

    if (this.isEnterpriseUser === 'true') {
      this.dashboardService.getAdminProfile().subscribe(companyProfile => {
        const fullSubstitutions = {
          ...substitutions,
          companyName: companyProfile?.name || 'Company',
          companyEmail: companyProfile?.email || 'support@evaltrix.com',
          companyPhone: companyProfile?.phone || 'N/A',
          logo: companyProfile?.logo || 'https://evaltrix.com/assets/EmailImg.png',
        };
        this.applySmartSubstitutions(fullSubstitutions);
      });
    } else {
      this.applySmartSubstitutions(substitutions);
    }
  }

  private applySmartSubstitutions(subs: { [key: string]: string }): void {
    const synonymMap: { [key: string]: string[] } = {
      jobTitle: ['role', 'position', 'jobRole'],
      companyName: ['enterpriseName', 'orgName', 'company', 'organizationName'],
      candidateName: ['name', 'userName'],
      companyPhone: ['phone', 'mobileNumber', 'orgPhoneNo'],
      companyEmail: ['email', 'supportEmail'],
      duration: ['time', 'allottedTime'],
    };

    // Initialize/Refresh placeholders
    let result = { ...this.placeholderDummies };

    Object.keys(subs).forEach(canonicalKey => {
      const value = subs[canonicalKey];
      const keysToMap = [canonicalKey, ...(synonymMap[canonicalKey] || [])];

      keysToMap.forEach(key => {
        // Match various casings for UI preview ({{key}}, {{KEY}}, {{Key_Name}})
        const parts = key.replace(/([a-z])([A-Z])/g, '$1_$2').split(/[_\s\-]+/);
        const underscore = `{{${parts.join('_')}}}`;
        const space = `{{${parts.join(' ')}}}`;
        const camel = `{{${key}}}`;
        const upper = `{{${key.toUpperCase()}}}`;

        result[underscore] = value;
        result[space] = value;
        result[camel] = value;
        result[upper] = value;
      });
    });
    this.placeholderDummies = result;
  }

  private formatMins(value: any): string {
    const n = Number(value) || 0;
    const unit = n === 1 ? 'min' : 'mins';
    return `${n}${unit}`;
  }

  proceedAssessment() {
    this.router.navigate(['candidate/assessment-categories'],
      { queryParams: { id: this.assessmentId, isEnterpriseUser: this.isEnterpriseUser } });
  }

  onWelcomeClick(event: MouseEvent) {
    const el = event.target as HTMLElement;
    if (!el) return;

    // Find nearest anchor (handles clicks on nested elements like <strong> inside the link)
    let anchor: HTMLElement | null = el;
    while (anchor && anchor.tagName !== 'A') {
      anchor = anchor.parentElement;
    }
    if (!anchor) return;

    const a = anchor as HTMLAnchorElement;
    const href = (a.getAttribute('href') || '').trim();
    const text = (a.textContent || '').trim().toLowerCase();

    // Heuristics to detect the "Begin/Start Assessment" CTA from the template:
    // - text includes "start your assessment" or "begin assessment"
    // - or href looks like the token start link (/candidate?token=...)
    const isCtaText = text.includes('start your assessment') || text.includes('begin assessment') || text.includes('start assessment');
    const isTokenHref = /\/candidate(\?|%3F)token=/.test(href);

    if (isCtaText || isTokenHref) {
      event.preventDefault();
      this.proceedAssessment();
    }
  }

}
